import React, { useState, useEffect } from 'react';
import {
  TextInput,
  SelectInput,
  Alert,
  Modal,
  Button,
  FormButton,
} from '@cochlear-design-system/foundation';
import patientService from '../../services/patientService';
import { getLabelsForFields } from '../../utils/labelHelper';
import { logger } from '../../logger';

import {
  getPreferredLanguage,
  longLangaugeCodeRegex,
} from '../../utils/patientCarerHelper';
import { getLanguageList } from '../../data/langaugeList';
import { EMAIL_REGEX } from '../../constants/constants';

const log = logger();

const AccountInviteModal = ({
  rendering,
  providerId,
  inviteData,
  setInviteData,
  showInviteModal,
  onInviteModalClose,
  inviteSendingInProgress,
  setInviteSendingInProgress,
  loggedInCountryCode,
}) => {
  const labels = getLabelsForFields(rendering.fields);
  const supportedLanguages = getLanguageList();
  const [langTouched, setLangTouched] = useState(false);
  const [showError, setShowError] = useState(false);
  const [hasDateOfBirth, setHasDateOfBirth] = useState(true);
  const [missingDoBLabels, setMissingDoBLablels] = useState({
    title:
      labels['labels.accountInvite.views.create.missingDoB.title'],
    message:
      labels['labels.accountInvite.views.create.missingDoB.message'],
  });
  const countryCode = loggedInCountryCode?.toUpperCase();
  const defaultLanguage = getPreferredLanguage();

  let emailErrorMsg = '';
  if (inviteData.email === '') {
    emailErrorMsg =
      labels['labels.accountInvite.views.resend.email.error'];
  } else if (!EMAIL_REGEX.test(inviteData.email)) {
    emailErrorMsg =
      labels['labels.accountInvite.views.resend.email.invalidEmail'];
  }

  const updateEmail = (e) => {
    const updatedData = {
      ...inviteData,
      email: e.target.value,
      emailUpdated: true,
    };
    setInviteData(updatedData);
  };

  const updateLanguage = (value) => {
    const updatedData = {
      ...inviteData,
      preferredLanguage: longLangaugeCodeRegex.test(value)
        ? value
        : `${value}-${countryCode}`,
    };
    setInviteData(updatedData);
  };

  const inviteTitle =
    labels[
      'labels.patientRecord.views.associatedCarersView.read.elements.accountDetails'
    ];
  const inviteDataValid = () => {
    const valid =
      hasDateOfBirth &&
      inviteData.email &&
      EMAIL_REGEX.test(inviteData.email) &&
      inviteData.preferredLanguage &&
      inviteData.preferredLanguage !== '';

    return !valid;
  };

  const onClose = async (send = false) => {
    setInviteSendingInProgress(true);
    if (send && !inviteData.resend) {
      try {
        const accountCheck = await patientService.checkAccountStatus(
          providerId,
          inviteData.cochlearId,
          inviteData.email,
        );

        if (accountCheck.accountStatus) {
          setInviteSendingInProgress(false);
          setShowError(true);
          setLangTouched(false);
          return;
        }
      } catch (err) {
        log.error('Error checking account status', err);
        setInviteSendingInProgress(false);
        setShowError(true);
        setLangTouched(false);
        return;
      }
    }

    // console.log('inviteData', inviteData);

    // if (inviteData.dateOfBirth === '') {
    //   setInviteData({
    //     ...inviteData,
    //     dateOfBirth: null,
    //   });
    // }

    if (onInviteModalClose) {
      onInviteModalClose(send);
    }
    setLangTouched(false);
    setShowError(false);
  };

  const onLangTouched = () => {
    setLangTouched(true);
  };

  useEffect(() => {
    if (
      supportedLanguages.some(
        (lang) => lang.value === defaultLanguage,
      )
    ) {
      updateLanguage(defaultLanguage);
    }
  }, [showInviteModal]);

  useEffect(() => {
    // For carers, DOB is optional, so skip validation
    if (inviteData?.accountFor?.toLowerCase() === 'carer') {
      setHasDateOfBirth(true);
      setShowError(false);
      setMissingDoBLablels({
        title: '',
        message: '',
      });
    } else if (
      inviteData.dateOfBirth === '' ||
      inviteData.dateOfBirth === null
    ) {
      // For patients (non-carers), DOB is required
      setHasDateOfBirth(false);
      setShowError(true);
    } else {
      setHasDateOfBirth(true);
      setShowError(false);
    }
  }, [inviteData]);

  return (
    <Modal
      title={inviteTitle}
      size="lg"
      show={showInviteModal}
      onHide={onClose}
      centered
    >
      <Modal.Header
        closeButton
        data-analytics="cancel_header_invite_modal"
      >
        <Modal.Title>{inviteTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div>
          {showError ? (
            <Alert
              show
              variant="error"
              heading={
                !hasDateOfBirth
                  ? missingDoBLabels.title
                  : labels[
                      'labels.accountInvite.views.create.duplicateEmail.title'
                    ]
              }
            >
              {!hasDateOfBirth
                ? missingDoBLabels.message
                : labels[
                    'labels.accountInvite.views.create.duplicateEmail.message'
                  ]}
            </Alert>
          ) : (
            <Alert show variant="brand">
              {labels['labels.accountInvite.views.resend.alertLabel']}
            </Alert>
          )}
          <TextInput
            hint={
              labels['labels.accountInvite.views.resend.email.hint']
            }
            label={
              labels[
                'labels.accountInvite.views.resend.email.inputLabel'
              ]
            }
            name="email"
            value={inviteData.email}
            readOnly={inviteData.resend}
            className="dpx__invite_modal_input"
            onChange={(e) => updateEmail(e)}
            onBlur={(e) => updateEmail(e)}
            error={
              inviteData.email === '' ||
              !EMAIL_REGEX.test(inviteData.email)
            }
            errorMsg={emailErrorMsg}
          />
          <SelectInput
            dataList={supportedLanguages}
            label={
              labels[
                'labels.accountInvite.views.resend.language.selectLabel'
              ]
            }
            name="preferredLang"
            promptText={
              labels[
                'labels.accountInvite.views.resend.language.prompt'
              ]
            }
            onChange={(_, value) => updateLanguage(value)}
            onBlur={onLangTouched}
            onFocus={onLangTouched}
            isSearchable
            defaultValue={defaultLanguage}
            error={
              langTouched &&
              (!inviteData.preferredLanguage ||
                inviteData.preferredLanguage === '')
            }
            errorMsg={emailErrorMsg}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <FormButton
          text={
            !inviteSendingInProgress
              ? labels['labels.accountInvite.views.resend.send']
              : labels['labels.accountInvite.views.resend.sending']
          }
          variant="brand-primary"
          disabled={inviteDataValid() || inviteSendingInProgress}
          onClick={() => onClose(true)}
          type="save"
          progress={inviteSendingInProgress ? 2 : undefined}
          data-analytics="send_invite_modal"
        />
        <Button
          onClick={() => onClose(false)}
          text={labels['labels.accountInvite.views.resend.cancel']}
          variant="tertiary"
          data-analytics="cancel_invite_modal"
        />
      </Modal.Footer>
    </Modal>
  );
};

export default AccountInviteModal;
